import {
  Alert,
  Box,
  Button,
  FormField,
  Heading,
  Label,
  Modal,
  Paragraph,
  Textarea,
  TextButton,
  Toast,
  useToast,
} from '@zillow/constellation-10';
import Image from 'next/image';
import type React from 'react';
import { useState } from 'react';
import { useSatelliteWebLandlordLeadManagementHandler_approveLead } from '@zg-rentals/ts-satellite-client-react-query';
import { CONVERSATION_MESSAGES_TAG } from '../../../hooks/useConversationMessagesQuery';
import approveLeadImage from '../../../images/approve-lead-celebration.svg';
import { useAuth } from '../../../AuthProvider';
import CharacterCountHelp from '../../CharacterCountHelp';
import {
  CLICK_SEND_MESSAGE_IN_APPROVE_LEAD_MODAL_EVENT_TYPE_ID,
  emitClickSendMessageInApproveLeadModalEvent,
} from '../../../constants/clickstreamEvents/approveLeadModal';

const MESSAGE_MAX_LENGTH = 500;

type ApproveLeadModalProps = {
  conversationId: string;
  name: string;
  streetAddress: string;
  listingAlias: string;
  leadId?: string | null;
  onClose: () => void;
};

export default function ApproveLeadModal({
  conversationId,
  name,
  streetAddress,
  listingAlias,
  leadId,
  onClose,
}: ApproveLeadModalProps) {
  const { enqueueToast } = useToast();
  const { user } = useAuth();
  const defaultMessage = `Hi ${name}! Your application has been approved for ${streetAddress}. I'll let you know next steps soon.`;

  const [message, setMessage] = useState(defaultMessage);
  const [isMessageFocused, setIsMessageFocused] = useState(false);
  const isMessageEmpty = message.trim().length === 0;
  const isMessageOverLimit = message.length > MESSAGE_MAX_LENGTH;

  const {
    mutateAsync: approveLead,
    isPending,
    isError,
  } = useSatelliteWebLandlordLeadManagementHandler_approveLead({
    meta: {
      // Invalidate the conversation messages query too so an open RenterMessagingModal
      // refetches and shows the approval message without a page refresh.
      invalidates: ['leadManagement:table', CONVERSATION_MESSAGES_TAG],
      context: {
        api: 'approveLead',
        details: 'ApproveLeadModal - approve lead and send message',
      },
    },
  });

  const handleSendMessage = async () => {
    emitClickSendMessageInApproveLeadModalEvent(user, { listingAlias }, { leadId });
    try {
      await approveLead({ postData: { conversationId, customMessage: message.trim() } });
      onClose();
      enqueueToast(<Toast tone="success">Approval sent</Toast>);
    } catch {
      // isError is tracked by React Query
    }
  };

  const handleMessageChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setMessage(event.target.value);
  };

  return (
    <Modal
      open={true}
      onOpenChange={(isOpen) => !isOpen && onClose()}
      dividers={false}
      size={{
        base: 'fullScreen',
        md: 'md',
      }}
      body={
        <Box>
          <Box css={{ marginBottom: 'default' }}>
            <Image src={approveLeadImage} alt="" width={160} height={160} />
          </Box>

          <Heading level={2} textStyle="heading-xs" css={{ marginBottom: 'default' }}>
            You&rsquo;re approving {name} for {streetAddress}
          </Heading>

          <Alert
            tone="info"
            css={{
              marginBottom: 'default',
            }}
          >
            You can approve multiple applicants if they meet your criteria, but you can only invite one person to move
            in
          </Alert>

          <Paragraph css={{ marginBottom: 'default' }}>
            Send a message to let them know the good news, and add any additional details you&rsquo;d like to include
            about how to move forward.
          </Paragraph>

          <FormField
            error={isMessageOverLimit}
            control={
              <Textarea
                onChange={handleMessageChange}
                rows={4}
                value={message}
                onFocus={() => setIsMessageFocused(true)}
                onBlur={() => setIsMessageFocused(false)}
                css={{ backgroundColor: 'bg.neutral', resize: 'vertical' }}
              />
            }
            description={
              <CharacterCountHelp
                currentLength={message.length}
                maxLength={MESSAGE_MAX_LENGTH}
                isFocused={isMessageFocused}
                label="Message"
              />
            }
            label={<Label>Customize your message</Label>}
            required
          />

          {isError && (
            <Alert tone="critical" css={{ marginTop: 'default' }}>
              Your message cannot be sent. Please try again.
            </Alert>
          )}
        </Box>
      }
      footer={
        <Box css={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 'default' }}>
          <TextButton tone="brand" onClick={onClose}>
            Close without approving
          </TextButton>
          <Button
            data-eventid={CLICK_SEND_MESSAGE_IN_APPROVE_LEAD_MODAL_EVENT_TYPE_ID}
            emphasis="filled"
            tone="brand"
            disabled={isMessageEmpty || isMessageOverLimit || isPending}
            onClick={handleSendMessage}
          >
            {isPending ? 'Sending...' : 'Send message'}
          </Button>
        </Box>
      }
    />
  );
}
